/**
 * state.js - Shared state for Three.js Configurator
 *
 * This module contains all shared state variables that are accessed
 * by multiple modules in the configurator system.
 */

import * as THREE from 'three';

// ===== SCENE & RENDERING =====
export let scene = null;
export let camera = null;
export let renderer = null;
export let controls = null;
export let floor = null;
export let grid = null;
export let fadePlane = null;
export let mainLight = null;
export let fakeShadow = null;
export let modelRoot = null; // gltf.scene des aktuell geladenen Modells, für Shadow-Neuberechnung nach Feature-Toggle

export const setScene = (s) => { scene = s; };
export const setCamera = (c) => { camera = c; };
export const setRenderer = (r) => { renderer = r; };
export const setControls = (ctrl) => { controls = ctrl; };
export const setFloor = (f) => { floor = f; };
export const setGrid = (g) => { grid = g; };
export const setFadePlane = (fp) => { fadePlane = fp; };
export const setMainLight = (ml) => { mainLight = ml; };
export const setFakeShadow = (fs) => { fakeShadow = fs; };
export const setModelRoot = (r) => { modelRoot = r; };

// ===== DECAL SYSTEM =====
export const lockedDecals = new Map();
export const sideDecals = new Map();
export const decalSettings = new Map();
export const decalMaterialsByGroup = new Map();
export const groupDecals = new Map();
export const groupDecalSettings = new Map();
export const lockedGroupDecals = new Map();

// ===== MATERIALS & MESHES =====
export const sideMats = new Map();
export const originalMaterials = new Map();
export const sideMeshes = new Map();

// Schaltbare Ausstattungsteile (z.B. Geländer, Terrasse), gruppiert per Node-Namenskonvention
// "configurator-<key>" (mehrere Meshes pro Gruppe via Blender-Suffix ".001" usw.). Sichtbarkeits-Toggle
// statt Modell-Reload, damit Decals/Grafiken beim Umschalten erhalten bleiben.
export const featureGroups = new Map();

export const markers = new Map();
export const markerHelpers = [];
export let allMeshes = [];
export let occludeTargets = [];

export const setAllMeshes = (meshes) => { allMeshes = meshes; };
export const setOccludeTargets = (targets) => { occludeTargets = targets; };

// ===== CUSTOM COLORS =====
export let customColors = new Set(); // Set of hex colors selected by user
export const MAX_CUSTOM_COLORS = 12; // Maximum number of custom colors to remember

export const addCustomColor = (hexColor) => {
    customColors.add(hexColor.toUpperCase());
    // Keep only last MAX_CUSTOM_COLORS
    if (customColors.size > MAX_CUSTOM_COLORS) {
        const arr = Array.from(customColors);
        customColors = new Set(arr.slice(-MAX_CUSTOM_COLORS));
    }
};

export const loadCustomColors = () => {
    // Custom colors are now session-only (not persisted)
    // Clean up any old localStorage data
    try {
        localStorage.removeItem('configurator_customColors');
    } catch (e) {
        // Ignore errors
    }
};

// ===== GRAPHICS LAYER SYSTEM (8 Layers) =====
export const graphicLayers = new Array(8).fill(null); // Array of {texture, centerUV, sizeUV, rotation, graphicId}
export const graphicLayerMap = new Map(); // graphicId -> layerIndex
export let uploadedGraphics = []; // Hochgeladene Grafiken
export let currentEditingGraphic = null; // Aktuell bearbeitete Grafik
export const viewportButtonsMap = new Map(); // graphicId -> {confirmBtn, deleteBtn} - Persistent button references
export const initializedMeshes = new Set(); // Cache für bereits initialisierte Meshes (Performance)

export const setUploadedGraphics = (graphics) => { uploadedGraphics = graphics; };
export const setCurrentEditingGraphic = (graphic) => { currentEditingGraphic = graphic; };

// ===== TEXTURE & DECAL STATE =====
export let pendingTexture = null;
export let pendingGroupTexture = null;
export let activeSide = null;
export let activeGroup = null;
export let activeSideKey = null;

export const setPendingTexture = (tex) => { pendingTexture = tex; };
export const setPendingGroupTexture = (tex) => { pendingGroupTexture = tex; };
export const setActiveSide = (side) => { activeSide = side; };
export const setActiveGroup = (group) => { activeGroup = group; };
export const setActiveSideKey = (key) => { activeSideKey = key; };

// ===== UI STATE =====
export let containerEl = null;
export let overlayEl = null;
export let decalHintShown = false;
export let hintOverlayEl = null;
export let hintShownTime = 0;
export let lastHit = null;

export const setContainerEl = (el) => { containerEl = el; };
export const setOverlayEl = (el) => { overlayEl = el; };
export const setDecalHintShown = (shown) => { decalHintShown = shown; };
export const setHintOverlayEl = (el) => { hintOverlayEl = el; };
export const setHintShownTime = (time) => { hintShownTime = time; };
export const setLastHit = (hit) => { lastHit = hit; };

// ===== DRAG & DROP STATE =====
export let isDraggingDecal = false;
export let dragStarted = false;
export let dragOffset = { x: 0, y: 0 }; // Offset zwischen Mausposition und Decal-Zentrum

export const setIsDraggingDecal = (dragging) => { isDraggingDecal = dragging; };
export const setDragStarted = (started) => { dragStarted = started; };
export const setDragOffset = (offset) => { dragOffset = offset; };

// ===== RAYCASTER & MARKERS =====
export const raycaster = new THREE.Raycaster();
raycaster.params.Points.threshold = 0.1;
raycaster.params.Line.threshold = 1;

export let lastMarkerUpdate = 0;
export const MARKER_UPDATE_INTERVAL = 50;
export const frustum = new THREE.Frustum();
export const cameraViewProjectionMatrix = new THREE.Matrix4();

export const setLastMarkerUpdate = (time) => { lastMarkerUpdate = time; };

// ===== CAMERA & ZOOM =====
export let zoomPercent = 100;
export let initialZoom = 100;
export let initialCamPos = new THREE.Vector3();
export let initialTarget = new THREE.Vector3();
export let initialCamDist = 0;

export const setZoomPercent = (percent) => { zoomPercent = percent; };
export const setInitialZoom = (percent) => { initialZoom = percent; };
export const setInitialCamPos = (pos) => { initialCamPos = pos; };
export const setInitialTarget = (target) => { initialTarget = target; };
export const setInitialCamDist = (dist) => { initialCamDist = dist; };

// ===== ANIMATION STATE =====
export let needsRender = true;
export let animationPaused = false;
export let needsMarkerUpdate = true;
export let lastCameraPosition = new THREE.Vector3();
export let lastCameraQuaternion = new THREE.Quaternion();
export let lastControlsTarget = new THREE.Vector3();
export let framesSinceChange = 0;
export const MAX_IDLE_FRAMES = 60;

export const setNeedsRender = (needs) => { needsRender = needs; };
export const setAnimationPaused = (paused) => { animationPaused = paused; };
export const setNeedsMarkerUpdate = (needs) => { needsMarkerUpdate = needs; };
export const setLastCameraPosition = (pos) => { lastCameraPosition = pos; };
export const setLastCameraQuaternion = (quat) => { lastCameraQuaternion = quat; };
export const setLastControlsTarget = (target) => { lastControlsTarget = target; };
export const setFramesSinceChange = (frames) => { framesSinceChange = frames; };

// ===== LOADING STATE =====
export let isLoading = false;
export const setIsLoading = (loading) => { isLoading = loading; };

// ===== TEXTURE CACHE =====
export let cachedCamoTexture = null;
export const setCachedCamoTexture = (texture) => { cachedCamoTexture = texture; };

// ===== CMS SETTINGS =====
// Wenn true, werden Decals auf Glas-Materialien voll deckend (opak) gerendert,
// ohne das Glas-Material selbst zu verändern. Wird aus dem CMS-Element gelesen.
export let glassDecalsOpaque = false;
export const setGlassDecalsOpaque = (value) => { glassDecalsOpaque = !!value; };

// ===== GLOBAL DUMMY TEXTURE =====
export let globalDummyTexture = null;
export const setGlobalDummyTexture = (texture) => { globalDummyTexture = texture; };

// ===== TEMP OBJECTS (for performance - reused) =====
export const _tempVector3 = new THREE.Vector3();
export const _tempVector3_2 = new THREE.Vector3();
export const _tempVector3_3 = new THREE.Vector3();
export const _tempVector2 = new THREE.Vector2();
export const _tempBox3 = new THREE.Box3();

// ===== MISSING LABELS TRACKING =====
export const _missingLabels = new Set();
